import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ChevronDown, ChevronUp, ExternalLink, Eye, Loader2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { ToolListDialog } from "~/pages/manifold-nextgen/components/agents/ToolListDialog";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentConnector,
  AgentScope,
  AgentToolkit,
  Connector,
  Runtime,
  Toolkit,
  agentConnectorsPath,
  agentPath,
  agentToolkitsPath,
  connectorToolsPath,
  connectorsPath,
  disableAgentConnector,
  disableAgentToolkit,
  enableAgentConnector,
  enableAgentToolkit,
  runtimesPath,
  toolkitToolsPath,
  toolkitsPath,
  updateAgent,
} from "~/services/agents";

// Mirrors the backend `ToolkitId.CODE_INTERPRETER`. Runtimes only take effect
// when this toolkit is mounted on the agent, so they're nested under its row
// here rather than shown as an unconditional, separate list.
const CODE_INTERPRETER_TOOLKIT_ID = "code_interpreter";

interface SectionProps {
  agent: Agent;
  canEdit: boolean;
}

interface CapabilitySectionHeaderProps {
  title: string;
  count?: number;
  max?: number;
  canEdit: boolean;
  description: string;
}

// A sub-section heading within Capabilities — sized to match the Behavior
// tab's field labels rather than TabHeader's full-page title.
const CapabilitySectionHeader = ({
  title,
  count,
  max,
  canEdit,
  description,
}: CapabilitySectionHeaderProps) => {
  const showCounter = count !== undefined && max !== undefined;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <h3 className="text-base font-medium text-foreground">{title}</h3>
        {showCounter && (
          <span className="text-xs text-muted-foreground">
            {canEdit ? (
              <>
                {count} of {max} enabled
              </>
            ) : (
              <>{count} enabled</>
            )}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
};

const RuntimesList = ({
  agent,
  canEdit,
  runtimes,
  isLoading,
}: SectionProps & {
  runtimes: Runtime[] | undefined;
  isLoading: boolean;
}) => {
  const { showError } = useToast();
  const [open, setOpen] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 px-4 py-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-8 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }

  if (!runtimes?.length) return null;

  // NULL means every customer runtime is allowed. Materialize it against the
  // catalog and drop ids the customer no longer offers, so a stale selection
  // can't inflate the count and unlock disabling the last effective runtime.
  const catalogIds = new Set(runtimes.map((r) => r.id));
  const selectedIds = (agent.runtime_ids ?? runtimes.map((r) => r.id)).filter((id) =>
    catalogIds.has(id)
  );
  const enabledIds = new Set(selectedIds);
  // Toggling rewrites the whole selection, so overlapping toggles race: lock
  // every switch while a PATCH is in flight, so the next toggle can't build on
  // pre-PATCH state and clobber the first write.
  const isToggling = togglingIds.size > 0;

  const handleToggle = async (runtime: Runtime, enabled: boolean) => {
    const nextIds = enabled
      ? [...selectedIds, runtime.id]
      : selectedIds.filter((id) => id !== runtime.id);
    if (nextIds.length === 0) return;

    // A selection covering every customer runtime normalizes back to NULL, so
    // the agent keeps auto-adopting any runtime the customer enables later.
    const nextSelection = runtimes.every((r) => nextIds.includes(r.id)) ? null : nextIds;

    setTogglingIds((prev) => new Set(prev).add(runtime.id));
    try {
      try {
        await updateAgent(agent.id, { runtime_ids: nextSelection });
      } catch {
        showError(`Failed to ${enabled ? "enable" : "disable"} "${runtime.name}" runtime`);
        return;
      }
      // Save persisted; revalidate before the switches re-enable. A refetch
      // failure hits the outer catch — not a toggle failure, so it must not
      // raise the toast above; SWR surfaces it via the list's own state.
      await mutate(agentPath(agent.id));
    } catch (error) {
      console.error("Failed to revalidate agent after toggling a runtime", error);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(runtime.id);
        return next;
      });
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-1.5 rounded-md px-4 pb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground">
        <span>Runtimes ({runtimes.length})</span>
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="ml-8 mb-3 flex flex-col gap-2 pr-4">
        {runtimes.map((runtime) => {
          const isEnabled = enabledIds.has(runtime.id);
          const isOnlyEnabled = isEnabled && enabledIds.size === 1;
          return (
            <div key={runtime.id} className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{runtime.name}</p>
                {runtime.description && (
                  <p className="text-xs text-muted-foreground">{runtime.description}</p>
                )}
              </div>
              {canEdit &&
                (togglingIds.has(runtime.id) ? (
                  <div className="flex h-[1.15rem] w-8 items-center justify-center">
                    <Loader2 className="size-3 animate-spin text-muted-foreground" />
                  </div>
                ) : isOnlyEnabled ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* Wrap the disabled Switch so it still fires hover
                       * events for the tooltip. */}
                      <span className="inline-flex">
                        <Switch
                          checked
                          disabled
                          aria-label={`${runtime.name} is the agent's only enabled runtime`}
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>At least one runtime must stay enabled</TooltipContent>
                  </Tooltip>
                ) : (
                  <Switch
                    checked={isEnabled}
                    disabled={isToggling}
                    onCheckedChange={(checked) => handleToggle(runtime, checked)}
                    aria-label={`${isEnabled ? "Disable" : "Enable"} ${runtime.name}`}
                  />
                ))}
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Planning is a durable per-agent capability, toggled like a toolkit but stored
// on the agent itself (via `updateAgent`, not a toolkit grant). It rides as the
// first row of the Toolkits list, so it has no "view tools" affordance and does
// not count toward the section's enabled tally.
const PlanningRow = ({ agent, canEdit }: SectionProps) => {
  const { showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // The toggle commits immediately — no separate Save step.
  const handlePlanningChange = async (next: boolean) => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, { planning_enabled: next });
      await mutate(agentPath(agent.id));
    } catch {
      showError("Failed to update planning");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">Planning Mode</p>
        <p className="text-xs text-muted-foreground">
          Allow this agent to create and maintain a plan for multi-step requests.
        </p>
      </div>
      <div className="flex items-center gap-1">
        {canEdit &&
          (isSaving ? (
            <div className="flex h-[1.15rem] w-8 items-center justify-center">
              <Loader2 className="size-3 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Switch
              checked={agent.planning_enabled}
              onCheckedChange={handlePlanningChange}
              aria-label={`${agent.planning_enabled ? "Disable" : "Enable"} Planning Mode`}
            />
          ))}
      </div>
    </div>
  );
};

const ToolkitsSection = ({ agent, canEdit }: SectionProps) => {
  const { showError } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [viewingToolkit, setViewingToolkit] = useState<Toolkit | null>(null);

  const { data: toolkits, isLoading: toolkitsLoading } = useFetcher<Toolkit[]>(toolkitsPath());
  const { data: agentToolkits, isLoading: agentToolkitsLoading } = useFetcher<AgentToolkit[]>(
    agentToolkitsPath(agent.id)
  );
  const { data: runtimes, isLoading: runtimesLoading } = useFetcher<Runtime[]>(runtimesPath());

  // A toolkit the user lacks scopes for is grayed out and not toggleable. The
  // catalog carries each toolkit's required scopes, so map each toolkit id to
  // the scopes it requires that the user does not hold.
  const user = useUser();
  const userPermissions = new Set(user?.permissions ?? []);
  const missingScopesById = new Map(
    (toolkits ?? []).map((t) => [
      t.id,
      t.required_scopes.filter((scope) => !userPermissions.has(scope)),
    ])
  );

  const enabledIds = new Set((agentToolkits ?? []).map((t) => t.toolkit.id));
  const isLoading = toolkitsLoading || agentToolkitsLoading;
  const totalCount = toolkits?.length ?? 0;
  const displayedToolkits = canEdit
    ? (toolkits ?? [])
    : (toolkits ?? []).filter((t) => enabledIds.has(t.id));

  const handleToggle = async (toolkit: Toolkit, enabled: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(toolkit.id));
    try {
      if (enabled) {
        await enableAgentToolkit(agent.id, toolkit.id);
      } else {
        await disableAgentToolkit(agent.id, toolkit.id);
      }
      await mutate(agentToolkitsPath(agent.id));
    } catch {
      showError(`Failed to ${enabled ? "enable" : "disable"} "${toolkit.display_name}" Toolkit`);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(toolkit.id);
        return next;
      });
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <CapabilitySectionHeader
        title="Toolkits"
        count={isLoading || totalCount === 0 ? undefined : enabledIds.size}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        canEdit={canEdit}
        description="Toolkits expose tools the agent can call during a conversation. Mount the ones necessary for this agent to do its job."
      />

      <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
        {/* In a read-only view, show Planning only when it is on — a disabled
            toggle is omitted, the same way an unenabled toolkit is hidden. An
            editor always sees the toggle so they can turn it on. */}
        {(canEdit || agent.planning_enabled) && <PlanningRow agent={agent} canEdit={canEdit} />}
        {isLoading ? (
          <div className="flex flex-col gap-3 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : !displayedToolkits.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {canEdit ? "No toolkits available" : "No toolkits enabled"}
          </p>
        ) : (
          displayedToolkits.map((toolkit) => {
            const isCodeInterpreter = toolkit.id === CODE_INTERPRETER_TOOLKIT_ID;
            const missingScopes = missingScopesById.get(toolkit.id) ?? [];
            const lacksScopes = missingScopes.length > 0;
            return (
              <div key={toolkit.id} className="flex flex-col">
                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{toolkit.display_name}</p>
                    {toolkit.description && (
                      <p className="text-xs text-muted-foreground">{toolkit.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setViewingToolkit(toolkit)}
                      aria-label={`View tools in ${toolkit.display_name}`}
                      title="View tools"
                      className="size-8 text-muted-foreground"
                    >
                      <Eye className="size-4" />
                    </Button>
                    {canEdit &&
                      (togglingIds.has(toolkit.id) ? (
                        <div className="flex h-[1.15rem] w-8 items-center justify-center">
                          <Loader2 className="size-3 animate-spin text-muted-foreground" />
                        </div>
                      ) : lacksScopes ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {/* Wrap the disabled Switch so it still fires hover
                             * events for the tooltip. */}
                            <span className="inline-flex">
                              <Switch
                                checked={enabledIds.has(toolkit.id)}
                                disabled
                                aria-label={`${toolkit.display_name} requires additional permissions`}
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Requires the {missingScopes.join(", ")} permission
                            {missingScopes.length > 1 ? "s" : ""}, which you don&apos;t have
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Switch
                          checked={enabledIds.has(toolkit.id)}
                          onCheckedChange={(checked) => handleToggle(toolkit, checked)}
                          aria-label={`${enabledIds.has(toolkit.id) ? "Disable" : "Enable"} ${toolkit.display_name}`}
                        />
                      ))}
                  </div>
                </div>
                {isCodeInterpreter && (canEdit || enabledIds.has(toolkit.id)) && (
                  <RuntimesList
                    agent={agent}
                    canEdit={canEdit}
                    runtimes={runtimes}
                    isLoading={runtimesLoading}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      <ToolListDialog
        open={!!viewingToolkit}
        onOpenChange={(open) => !open && setViewingToolkit(null)}
        title={viewingToolkit?.display_name ?? ""}
        description={viewingToolkit?.description}
        fetchPath={viewingToolkit ? toolkitToolsPath(viewingToolkit.id) : null}
      />
    </section>
  );
};

const ConnectorsSection = ({ agent, canEdit }: SectionProps) => {
  const { showError } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [viewingConnector, setViewingConnector] = useState<Connector | null>(null);

  const { data: connectors, isLoading: connectorsLoading } =
    useFetcher<Connector[]>(connectorsPath());
  const { data: agentConnectors, isLoading: agentConnectorsLoading } = useFetcher<AgentConnector[]>(
    agentConnectorsPath(agent.id)
  );

  const enabledIds = new Set((agentConnectors ?? []).map((c) => c.connector.id));
  const isLoading = connectorsLoading || agentConnectorsLoading;
  const totalCount = connectors?.length ?? 0;
  const displayedConnectors = canEdit
    ? (connectors ?? [])
    : (connectors ?? []).filter((c) => enabledIds.has(c.id));

  const handleToggle = async (connector: Connector, enabled: boolean) => {
    const label = connector.display_name ?? connector.name;
    setTogglingIds((prev) => new Set(prev).add(connector.id));
    try {
      if (enabled) {
        await enableAgentConnector(agent.id, connector.id);
      } else {
        await disableAgentConnector(agent.id, connector.id);
      }
      await mutate(agentConnectorsPath(agent.id));
    } catch {
      showError(`Failed to ${enabled ? "enable" : "disable"} "${label}" Connector`);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(connector.id);
        return next;
      });
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <CapabilitySectionHeader
        title="Connectors"
        count={isLoading || totalCount === 0 ? undefined : enabledIds.size}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        canEdit={canEdit}
        description="Connectors expose tools hosted on external MCP servers. Attach the ones this agent needs to do its job."
      />

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : !displayedConnectors.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {canEdit ? "No connectors available" : "No connectors enabled"}
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {displayedConnectors.map((connector) => {
            const label = connector.display_name ?? connector.name;
            return (
              <div key={connector.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{label}</p>
                  {(connector.description || connector.docs_url) && (
                    <p className="text-xs text-muted-foreground">
                      {connector.description}
                      {connector.docs_url && (
                        <>
                          {" "}
                          <a
                            href={connector.docs_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 underline hover:text-foreground"
                          >
                            Learn more
                            <ExternalLink className="size-3" />
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setViewingConnector(connector)}
                    aria-label={`View tools in ${label}`}
                    title="View tools"
                    className="size-8 text-muted-foreground"
                  >
                    <Eye className="size-4" />
                  </Button>
                  {canEdit &&
                    (togglingIds.has(connector.id) ? (
                      <div className="flex h-[1.15rem] w-8 items-center justify-center">
                        <Loader2 className="size-3 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <Switch
                        checked={enabledIds.has(connector.id)}
                        onCheckedChange={(checked) => handleToggle(connector, checked)}
                        aria-label={`${enabledIds.has(connector.id) ? "Disable" : "Enable"} ${label}`}
                      />
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ToolListDialog
        open={!!viewingConnector}
        onOpenChange={(open) => !open && setViewingConnector(null)}
        title={viewingConnector?.display_name ?? viewingConnector?.name ?? ""}
        description={viewingConnector?.description}
        fetchPath={viewingConnector ? connectorToolsPath(viewingConnector.id) : null}
      />
    </section>
  );
};

interface AgentCapabilitiesTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view (no edit affordances, no tab
  // title — the entity name is already shown in the sidebar header).
  sessionSidebarView?: boolean;
}

export const AgentCapabilitiesTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentCapabilitiesTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const canEdit = !sessionSidebarView && canEditGrant;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader
        title={sessionSidebarView ? undefined : "Capabilities"}
        showReadOnlyBadge={!sessionSidebarView && agent.scope === AgentScope.SYSTEM}
      />

      <ToolkitsSection agent={agent} canEdit={canEdit} />
      <ConnectorsSection agent={agent} canEdit={canEdit} />
    </div>
  );
};
